import { useCallback, useEffect, useMemo, useState } from "react";
import { OverlayTrigger } from "react-bootstrap";
import { Placement } from "react-bootstrap/esm/types";
import Popover from "react-bootstrap/Popover";

import classNames from "classnames";
import { isUndefined, uniqBy } from "lodash";
import { twMerge } from "tailwind-merge";
import { useDebounce } from "usehooks-ts";

import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";

import { SKSpinner } from "components/customElements/skSpinner";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import Input from "components/Input";
import { useEffectOnce } from "hooks/useEffectOnce";
import useInfiniteList from "hooks/useInfiniteList";

interface Option {
  value: string | number | undefined;
  label: string;
  className?: string;
}

interface Props {
  label: string;
  options?: Option[];
  endpoint?: (
    _pageIndex: number,
    _filter?: string
  ) => string | undefined | null;
  dataTransform?: (_item: any) => Option;
  selected?: Option[] | Option;
  multiple?: boolean;
  footer?: React.ReactNode;
  emptyState?: React.ReactNode;
  onSelect?: (_option: Option | Option[]) => void;
  dropdownClassName?: string;
  narrow?: boolean;

  // TODO: remove
  filters?: { [key: string]: string | number | undefined };
  filterKey: string;
  containerClassName?: string;
  optionContainerClassName?: string;
  optionListContainerClassName?: string;
  enableSearch?: boolean;
  className?: string;
  caretClassName?: string;
  placement?: Placement;
  showSelectionCount?: boolean;
  hideSelectedOnTop?: boolean;
  showSelectionCountZero?: boolean;
  hideFilter?: boolean;
  hideSelectCountOption?: boolean;
  showSelectedItemOnTop?: boolean;
}

export const CUSTOM_OPTION = "##custom##";

export const InfiniteListDropdown = ({
  endpoint,
  dataTransform,
  filter,
  renderDropdwonItem,
  onLoaded,
  emptyState,
}: Props & {
  filter?: string;
  renderDropdwonItem: (_option: Option) => React.ReactNode;
  onLoaded?: (_data: any) => void;
}) => {
  const { infiniteList } = useInfiniteList(
    (pageIndex) => {
      return endpoint?.(pageIndex + 1, filter);
    },
    {
      pageRender(pageData) {
        const uniqueData = uniqBy(pageData.data, (i: any) => i.id);

        return uniqueData?.map((item: any) => {
          return renderDropdwonItem(
            dataTransform
              ? dataTransform(item)
              : { value: item.name, label: item.name }
          );
        });
      },
      swrOptions: {
        revalidateOnFocus: false,
        onSuccess(rawData: any) {
          onLoaded?.(rawData);
        },
      },
      loadingIndicator() {
        return (
          <div className="flex justify-center pb-[15px]">
            <SKSpinner />
          </div>
        );
      },
      emptyIndicator() {
        if (emptyState) {
          return emptyState;
        }

        return <div className="flex justify-center py-[15px]">No Data</div>;
      },
      useEndRef: true,
    }
  );

  return infiniteList;
};

export default function ToolDropdownSelector({
  label,
  options,
  endpoint,
  dataTransform,
  selected,
  multiple,
  onSelect,
  footer,
  filters,
  filterKey,
  dropdownClassName,
  containerClassName,
  emptyState,
  narrow,
  hideSelectedOnTop,
  hideFilter,
  hideSelectCountOption,
  showSelectedItemOnTop,
}: Props) {
  const [open, setOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<any>(
    multiple ? [] : options?.[0]
  );
  const [filter, setFilter] = useState("");
  const debouncedFilter = useDebounce(filter, 300);
  const [totalItemsCount, setTotalItemsCount] = useState(options?.length || 0);
  const [itemsLoaded, setItemsLoaded] = useState(false);

  const filteredOptions = useMemo(() => {
    if (!debouncedFilter) return options;

    return options?.filter((item) => {
      const label = item.label.toLowerCase();
      return label.includes(debouncedFilter?.toLowerCase());
    });
  }, [debouncedFilter, options]);

  const filteredSelectedOptions = useMemo(() => {
    if (!debouncedFilter) return selectedOption;

    return selectedOption?.filter((item: Option) => {
      const label = item.label.toLowerCase();
      return label.includes(debouncedFilter?.toLowerCase());
    });
  }, [debouncedFilter, selectedOption]);

  useEffectOnce(
    (ack) => {
      ack();
      setSelectedOption(selected || (multiple ? [] : options?.[0]));
    },
    [multiple, options, selected]
  );

  const isSelected = useCallback(
    (option) => {
      if (multiple) {
        return (selectedOption as Option[])?.some(
          (o: Option) => o.value === option.value
        );
      }

      return (selectedOption as Option)?.value === option.value;
    },
    [multiple, selectedOption]
  );

  const onSelectOption = useCallback(
    (option: any) => {
      let newSelected = option;
      if (multiple) {
        if (selectedOption?.some((o: Option) => o.value === option.value)) {
          newSelected = selectedOption.filter(
            (item: any) => item.value !== option.value
          );
        } else {
          newSelected = [...(selectedOption || []), option];
        }
      }

      setSelectedOption(newSelected);
      onSelect?.(newSelected);
    },
    [multiple, onSelect, selectedOption]
  );

  const onSelectOnly = useCallback(
    (option: any) => {
      const newSelected = [option];
      setSelectedOption(newSelected);
      onSelect?.(newSelected);
    },
    [onSelect]
  );

  const onSelectAll = useCallback(() => {
    if (options) {
      if (selectedOption?.length === options.length) {
        setSelectedOption([]);
        onSelect?.([]);
      } else {
        setSelectedOption(options);
        onSelect?.(options);
      }
    }
  }, [onSelect, options, selectedOption?.length]);

  const isAllSelected = useMemo(() => {
    if (options) {
      if (multiple) {
        return selectedOption?.length === options.length;
      }
    }
  }, [multiple, options, selectedOption?.length]);

  useEffect(() => {
    if (totalItemsCount !== options?.length) {
      setTotalItemsCount(options?.length || 0);
    }
  }, [options, totalItemsCount]);

  useEffect(() => {
    if (isUndefined(filters?.[filterKey])) {
      setSelectedOption(multiple ? options : selected);
    }
  }, [filterKey, filters, multiple, options, selected]);

  const renderDropdownItem = useCallback(
    (option: Option) => {
      const selected = isSelected(option);
      return (
        <div
          key={option.label}
          className={twMerge(
            "flex items-center justify-between group hover:bg-yellow-100 cursor-pointer",
            option.className
          )}
        >
          <div
            className="flex items-center flex-1 pl-[10px] py-[10px] max-w-[calc(100%-50px)]"
            onClick={() => onSelectOption(option)}
          >
            <div
              className={classNames(
                "flex mr-[10px] items-center justify-center min-w-[20px] min-h-[20px]",
                multiple ? "rounded" : "rounded-full",
                selected && "bg-smart-teal-500 ",
                !selected && "border-[2px] border-solid border-slate-400"
              )}
            >
              {selected && (
                <FontAwesomeIcon
                  icon={faCheck}
                  color="white"
                  className="text-[10px]"
                />
              )}
            </div>
            <span className="truncate">{option.label}</span>
          </div>
          {multiple && (
            <span
              onClick={() => onSelectOnly(option)}
              className="underline md:hidden mr-[10px] md:group-hover:block text-[10px] p-[2px] px-[5px] bg-black text-white"
            >
              Only
            </span>
          )}
        </div>
      );
    },
    [isSelected, multiple, onSelectOnly, onSelectOption]
  );

  const dropdownItems = useMemo(() => {
    if (options) {
      if (showSelectedItemOnTop) {
        return filteredOptions
          ?.filter((o) => !isSelected(o))
          ?.map(renderDropdownItem);
      }

      return filteredOptions?.map(renderDropdownItem);
    }

    if (endpoint) {
      return (
        <InfiniteListDropdown
          {...{
            label,
            endpoint,
            dataTransform,
            selected,
            multiple,
            onSelect,
            footer,
            emptyState,
            filters,
            filterKey,
            filter: debouncedFilter,
            renderDropdwonItem: renderDropdownItem,
            onLoaded(rawData) {
              setTotalItemsCount(rawData?.[0]?.meta?.recordCount);
              setItemsLoaded(true);
            },
          }}
        />
      );
    }
  }, [
    dataTransform,
    debouncedFilter,
    emptyState,
    endpoint,
    filterKey,
    filteredOptions,
    filters,
    footer,
    isSelected,
    label,
    multiple,
    onSelect,
    options,
    renderDropdownItem,
    selected,
    showSelectedItemOnTop,
  ]);

  return (
    <div
      className={twMerge("w-[200px] relative select-none", containerClassName)}
    >
      <OverlayTrigger
        placement={"bottom"}
        trigger={"click"}
        rootClose
        show={open}
        onExit={() => {
          setOpen(false);
        }}
        onToggle={(data) => {
          if (!data) {
            setOpen(data);
          }
        }}
        overlay={
          <Popover className="overflow-hidden select-none !max-w-none">
            <Popover.Body
              className={twMerge(
                "!p-0 max-h-[310px] w-[200px] overflow-y-scroll",
                dropdownClassName
              )}
            >
              {multiple && (
                <div className="">
                  {!hideFilter && (
                    <div className="flex w-[100%] items-center p-[10px]">
                      <Input
                        onChange={(e) => {
                          setFilter(e.target.value);
                        }}
                        value={filter}
                        inputClassName="w-[100%]"
                        placeholder={`Search ${label}`}
                        className="w-[100%]"
                      />
                    </div>
                  )}
                  {endpoint &&
                    !hideSelectedOnTop &&
                    selectedOption?.map(renderDropdownItem)}
                  {!hideSelectCountOption && (
                    <div className="flex items-center p-[10px] bg-inactive-gray">
                      {options && (
                        <div
                          className={classNames(
                            "flex mr-[10px] items-center justify-center rounded min-w-[20px] min-h-[20px] cursor-pointer",
                            isAllSelected && "bg-smart-teal-500 ",
                            !isAllSelected &&
                              "border-[2px] border-solid border-slate-400"
                          )}
                          onClick={onSelectAll}
                        >
                          {isAllSelected && (
                            <FontAwesomeIcon
                              icon={faCheck}
                              color="white"
                              className="text-[10px]"
                            />
                          )}
                        </div>
                      )}
                      {(options || itemsLoaded) && (
                        <>
                          {(selectedOption?.length || totalItemsCount) ===
                          totalItemsCount
                            ? "All"
                            : selectedOption?.length || totalItemsCount}{" "}
                          Selected
                        </>
                      )}
                    </div>
                  )}
                  {showSelectedItemOnTop && (
                    <div>
                      {filteredSelectedOptions?.map(renderDropdownItem)}
                    </div>
                  )}
                </div>
              )}
              {dropdownItems}
              {selectedOption?.value === CUSTOM_OPTION && footer}
            </Popover.Body>
          </Popover>
        }
      >
        <div
          className={twMerge(
            "p-[10px] text-[12px] shadow-md rounded flex items-center justify-between cursor-pointer"
          )}
          onClick={() => setOpen(!open)}
        >
          <label className="truncate">
            {label}
            {narrow ? <br /> : " "}
            {multiple
              ? options || itemsLoaded
                ? `(${
                    (selectedOption?.length || totalItemsCount) ===
                    totalItemsCount
                      ? "All"
                      : `${selectedOption?.length} Selected`
                  })`
                : selectedOption?.length
                ? `(${selectedOption?.length} Selected)`
                : "(All)"
              : selectedOption?.valueLabel && `(${selectedOption?.valueLabel})`}
          </label>
          <FontAwesomeIcon
            icon={faCaretDown}
            className={twMerge("ml-[10px]", open && "transform rotate-180")}
          />
        </div>
      </OverlayTrigger>
    </div>
  );
}
